import React, { useState, useRef, useEffect } from "react";
import {
  Fab, Paper, Box, Typography, IconButton, TextField, Button,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import api from "../../api/api";

const SESSION_STORAGE_KEY = "chat_session_id";

// WhatsApp brand green, and how far from the viewport edges the widget sits
// (bottom offset clears the site footer instead of floating over it).
const WHATSAPP_GREEN = "#25D366";
const WHATSAPP_GREEN_DARK = "#1EBE5D";
const CM_IN_PX = 38; // ~1cm at standard 96dpi
const FAB_BOTTOM_OFFSET = 96 + CM_IN_PX;
const FAB_RIGHT_OFFSET = 20 + CM_IN_PX;

function getOrCreateSessionId() {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

const ChatWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: t("chat_welcome") },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(getOrCreateSessionId());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const sendMessage = async (message) => {
    const trimmed = message.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await api.post("/chat/message/", {
        message: trimmed,
        session_id: sessionIdRef.current,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("chat_error") },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <Box sx={{ position: "fixed", bottom: FAB_BOTTOM_OFFSET, right: FAB_RIGHT_OFFSET, zIndex: 1300 }}>
      {isOpen && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            bottom: 64,
            right: 0,
            width: 360,
            maxWidth: "calc(100vw - 32px)",
            height: 480,
            maxHeight: "calc(100vh - 140px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {t("chat_title")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              aria-label={t("chat_close")}
              sx={{ color: "inherit" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            role="log"
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              bgcolor: "background.default",
            }}
          >
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "80%",
                  bgcolor: msg.role === "user" ? "primary.main" : "background.paper",
                  color: msg.role === "user" ? "primary.contrastText" : "text.primary",
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: msg.role === "user" ? "none" : 1,
                  whiteSpace: "pre-wrap",
                }}
              >
                {msg.content}
              </Box>
            ))}
            {isLoading && (
              <Box sx={{ alignSelf: "flex-start", p: 1 }}>
                <CircularProgress size={18} />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: 1.5,
              display: "flex",
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <TextField
              inputRef={inputRef}
              size="small"
              fullWidth
              placeholder={t("chat_placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !input.trim()}
              sx={{ minWidth: 0, px: 1.5 }}
              aria-label={t("chat_send")}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
        </Paper>
      )}

      <Fab
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? t("chat_close") : t("chat_open")}
        sx={{
          bgcolor: WHATSAPP_GREEN,
          color: "#fff",
          "&:hover": { bgcolor: WHATSAPP_GREEN_DARK },
        }}
      >
        {isOpen ? <CloseIcon /> : <WhatsAppIcon />}
      </Fab>
    </Box>
  );
};

export default ChatWidget;
